import RiAddLine from '@ben/icons/add-line';
import RiSubtractLine from '@ben/icons/subtract-line';
import Currency from '@ben/orion/currency';
import IconButton from '@ben/orion/icon-button';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { WidgetProps } from '@rjsf/utils';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';

export interface ContributionsValue {
  employeePercentage?: number;
  employerPercentage?: number;
  employeeAmount?: number;
  employerAmount?: number;
  currency?: string;
}

interface ContributionsWidgetProps extends Omit<WidgetProps, 'value'> {
  value: ContributionsValue | undefined;
}

interface ContributionsSchemaProperties {
  employeePercentage?: {
    minimum?: number;
    maximum?: number;
    title?: string;
    description?: string;
  };
  employerPercentage?: {
    title?: string;
    description?: string;
  };
  showTotal?: {
    default?: boolean;
  };
  currency?: {
    default?: string;
  };
}

const ContributionsWidget = ({
  value,
  onChange,
  schema,
  readonly,
}: ContributionsWidgetProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const properties =
    schema.properties as unknown as ContributionsSchemaProperties;
  const employeePercentageProps = properties.employeePercentage ?? {};
  const employerPercentageProps = properties.employerPercentage ?? {};
  const showTotal = properties.showTotal?.default ?? true;
  const currencyCode = value?.currency || properties.currency?.default;

  const minValue = employeePercentageProps.minimum ?? 0;
  const maxValue = employeePercentageProps.maximum ?? 100;

  const constrainPercentage = useCallback(
    (percentage: number) => Math.max(Math.min(percentage, maxValue), minValue),
    [minValue, maxValue]
  );

  const {
    employeePercentage: originalEmployeePercentage,
    employerPercentage,
    employeeAmount,
    employerAmount,
  } = value || {};

  const employeePercentage = constrainPercentage(
    originalEmployeePercentage ?? 0
  );

  const hasEmployeeData =
    employeePercentage !== undefined || employeeAmount !== undefined;
  const hasEmployerData =
    employerPercentage !== undefined || employerAmount !== undefined;

  const totalPercentage = (employeePercentage || 0) + (employerPercentage || 0);
  const totalAmount =
    Number.parseFloat(String(employeeAmount || 0)) +
    Number.parseFloat(String(employerAmount || 0));

  const handleEmployeePercentageChange = useCallback(
    (newPercentage: number) => {
      const validPercentage = constrainPercentage(newPercentage);
      onChange({
        ...value,
        employeePercentage: validPercentage,
      });
    },
    [onChange, value, constrainPercentage]
  );

  const handleMinus = useCallback(() => {
    if (readonly) return;
    const newValue = Math.max((employeePercentage || 0) - 1, minValue);
    handleEmployeePercentageChange(newValue);
  }, [readonly, employeePercentage, handleEmployeePercentageChange, minValue]);

  const handlePlus = useCallback(() => {
    if (readonly) return;
    const newValue = Math.min((employeePercentage || 0) + 1, maxValue);
    handleEmployeePercentageChange(newValue);
  }, [readonly, employeePercentage, handleEmployeePercentageChange, maxValue]);

  return (
    <Wrapper>
      {hasEmployeeData && (
        <SummaryRow>
          <LabelContainer>
            <Typography variant={TypographyVariant.bodyLg1}>
              {employeePercentageProps.title || t('words.employee')}
            </Typography>
            <Typography
              variant={TypographyVariant.bodySm1}
              color={theme.palette.neutral[90]}
            >
              {employeePercentageProps.description}
            </Typography>
          </LabelContainer>
          <Values>
            <PercentageColumn>
              <StepperContainer>
                <IconButton
                  size="md"
                  icon={RiSubtractLine}
                  disabled={readonly || (employeePercentage || 0) <= minValue}
                  onClick={handleMinus}
                  ariaLabel={t('words.decrease')}
                />
                <Typography variant={TypographyVariant.bodyMd3}>
                  {`${employeePercentage || 0}%`}
                </Typography>
                <IconButton
                  size="md"
                  icon={RiAddLine}
                  disabled={readonly || (employeePercentage || 0) >= maxValue}
                  onClick={handlePlus}
                  ariaLabel={t('words.increase')}
                />
              </StepperContainer>
            </PercentageColumn>
            <Amount
              value={employeeAmount}
              currency={currencyCode}
              showPlus
              color={theme.palette.success.main}
            />
          </Values>
        </SummaryRow>
      )}

      {hasEmployerData && (
        <SummaryRow>
          <LabelContainer>
            <Typography variant={TypographyVariant.bodyLg1}>
              {employerPercentageProps.title || t('words.employer')}
            </Typography>
            <Typography
              variant={TypographyVariant.bodySm1}
              color={theme.palette.neutral[90]}
            >
              {employerPercentageProps.description}
            </Typography>
          </LabelContainer>
          <Values>
            <PercentageColumn>
              <Typography variant={TypographyVariant.bodyMd3}>
                {employerPercentage === undefined
                  ? '-'
                  : `${employerPercentage}%`}
              </Typography>
            </PercentageColumn>
            <Amount
              value={employerAmount}
              currency={currencyCode}
              showPlus
              color={theme.palette.success.main}
            />
          </Values>
        </SummaryRow>
      )}

      {showTotal && (hasEmployeeData || hasEmployerData) && (
        <TotalRow>
          <Typography variant={TypographyVariant.bodyLg3}>
            {t('words.Total')}
          </Typography>
          <Values>
            <PercentageColumn>
              <Typography variant={TypographyVariant.bodyMd3}>
                {`${totalPercentage}%`}
              </Typography>
            </PercentageColumn>
            <Amount
              value={totalAmount}
              currency={currencyCode}
              showPlus
              color={theme.palette.success.main}
              size="sm"
            />
          </Values>
        </TotalRow>
      )}
    </Wrapper>
  );
};

export default ContributionsWidget;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1.5)};
  `}
`;

const SummaryRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-right: ${theme.spacing(2)};
  `}
`;

const TotalRow = styled(SummaryRow)`
  ${({ theme }) => css`
    background-color: ${theme.palette.primary.bg};
    padding: ${theme.spacing(1, 2)};
    border-radius: ${theme.borderRadius.sm};
    margin-top: ${theme.spacing(1.5)};
  `}
`;

const LabelContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
  `}
`;

const Values = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(4)};
    align-items: center;
  `}
`;

const Amount = styled(Currency)`
  ${({ theme }) => css`
    min-width: ${theme.spacing(15)};
    text-align: right;
  `}
`;

const StepperContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const PercentageColumn = styled.div`
  ${({ theme }) => css`
    min-width: ${theme.spacing(15)};
    display: flex;
    justify-content: center;
    align-items: center;
  `}
`;
